import React, { useCallback } from 'react'
import { BaseCardProps, cardViewabilityDebug } from '../Card'
import {
    createCollectionAvailableEvent,
    isPublicationCardItemWithVideo,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'
import {
    Teaser,
    CardContainer,
    StyledCardMedia,
    ReadMore,
    Byline,
} from '../components'
import {
    StyledHeadline,
    StyledCardBodyWrapper,
    CardLinkWrapper,
} from './ListCard.styled'
import { getByline, getTopicLink } from '../utils'
import {
    EditorialType,
    FundingType,
    getEditorialType,
    getFundingType,
} from '../../../templates'
import { TNLiveComponent } from '../../../content'
import { useProduct } from '../../../__product'
import { useImpressionAvailable } from '../../../__helpers/impression-available-helper'
import { BylineFundingTypeWrapper } from '../../../publication/Byline/components/TheNightly/Byline.styled'
import { FundingLabel } from '../../../advertising/FundingLabel/FundingLabel'
import { StyledReadMoreWrapper } from '../components/card-body/ReadMore.styled'
import { isLiveEvent } from '../../../__helpers/is-live-event'
import { PublishedTime } from '../components/card-body/PublishedTime'

export interface ListCardProps extends BaseCardProps<'list-card'> {
    canPlayVideoInline?: boolean
}

export const ListCard = ({
    item,
    onEvent,
    canPlayVideoInline,
    disableImageLazyLoad,
    imageFetchPriority,
}: Omit<ListCardProps, 'kind'>) => {
    const classificationEnabled = true
    const product = useProduct()

    const { loaded: isLoaded } = item
    const result = isLoaded ? item.result : undefined
    const articleLink = toLinkWithHint(item)
    const byline = getByline(item)
    const topicLink = getTopicLink(item)
    const editorialType = isLoaded
        ? getEditorialType(item.result, classificationEnabled)
        : undefined
    const fundingType = isLoaded
        ? getFundingType(item.result, classificationEnabled)
        : undefined
    const isVideo = result && isPublicationCardItemWithVideo(result)

    const timestamp = result?.lastUpdated ?? result?.publicationDate
    const isUpdated = timestamp !== result?.publicationDate

    // Impression Available Function
    const availableFunction = useCallback(() => {
        if (!isLoaded || result === undefined) {
            console.warn(
                'Available Function should never be called when loading is true',
            )
            return
        }

        // Add a debug for viewability!
        cardViewabilityDebug(
            'ListCard',
            `in frame and has been viewed with a size of ${1}`,
        )

        // Send an onEvent when the items are available in view
        onEvent(
            createCollectionAvailableEvent(
                [result],
                'ListCard',
                product,
                onEvent,
            ),
        )
    }, [isLoaded, onEvent, product, result])

    // Impression Available Ref
    const impressionAvailableRef = useImpressionAvailable({
        loading: !isLoaded,
        available: availableFunction,
        debug: cardViewabilityDebug.extend('list-card'),
    })

    return (
        <CardContainer ref={impressionAvailableRef}>
            {isVideo && canPlayVideoInline ? (
                <ListCardMedia
                    item={item}
                    onEvent={onEvent}
                    canPlayVideoInline={canPlayVideoInline}
                    disableImageLazyLoad={disableImageLazyLoad}
                    imageFetchPriority={imageFetchPriority}
                />
            ) : (
                <CardLinkWrapper to={articleLink}>
                    <ListCardMedia
                        item={item}
                        onEvent={onEvent}
                        disableImageLazyLoad={disableImageLazyLoad}
                        imageFetchPriority={'high'}
                        classification={editorialType}
                    />
                </CardLinkWrapper>
            )}
            {result && (
                <StyledCardBodyWrapper>
                    <BylineFundingTypeWrapper>
                        <Byline
                            editorialType={editorialType}
                            topicLink={topicLink}
                            topicTitle={result.primaryTopic.title}
                            bylineText={byline.text}
                            bylineLink={byline.link}
                            allowCommenting={result.allowCommenting}
                            articleUrl={result._self}
                        />
                        {fundingType && (
                            <FundingLabel
                                fundingType={fundingType}
                                linkToDisclaimer={true}
                            >
                                {fundingType}
                            </FundingLabel>
                        )}
                    </BylineFundingTypeWrapper>
                    <CardLinkWrapper to={articleLink}>
                        <StyledHeadline>{result.shortHeadline}</StyledHeadline>
                        <Teaser>{result.teaser}</Teaser>
                        <StyledReadMoreWrapper>
                            <PublishedTime
                                publicationDate={timestamp}
                                isUpdatedTimestamp={isUpdated}
                            />
                            <ReadMore readTime={result.readTimeMinutes} />
                        </StyledReadMoreWrapper>
                    </CardLinkWrapper>
                </StyledCardBodyWrapper>
            )}
        </CardContainer>
    )
}

type ListCardMediaProps = Pick<
    BaseCardProps<any>,
    'onEvent' | 'item' | 'disableImageLazyLoad' | 'imageFetchPriority'
> & {
    canPlayVideoInline?: boolean
    classification?: EditorialType | FundingType | undefined
}

export const ListCardMedia = ({
    item,
    onEvent,
    canPlayVideoInline,
    disableImageLazyLoad,
    imageFetchPriority,
    classification,
}: ListCardMediaProps) => {
    const liveClassification = classification === EditorialType.Live

    return (
        <>
            {liveClassification && (
                <TNLiveComponent overlayOnImage={true} showBackground={true} />
            )}
            <StyledCardMedia
                fixedRatio={{
                    default: '4:3',
                    sm: canPlayVideoInline ? '16:9' : undefined,
                }}
                CSSCrop={{
                    default: {
                        width: 770,
                        height: 577,
                    },
                }}
                imageWidths={{
                    desktop: '100%',
                    tablet: '100%',
                    mobile: '100%',
                    fallbackWidth: 770,
                }}
                item={item}
                onEvent={onEvent}
                disableImageLazyLoad={disableImageLazyLoad}
                imageFetchPriority={imageFetchPriority}
                mediaMode={'fullHeight'}
                isLarge={canPlayVideoInline}
                willPlayVideoInline={canPlayVideoInline}
            />
        </>
    )
}
